import { z } from 'zod';

export const RoleSchema = z.enum(['SYSTEM_ADMIN', 'TENANT_ADMIN', 'PHYSICIAN', 'NURSE', 'FRONT_DESK', 'PATIENT']);
export type Role = z.infer<typeof RoleSchema>;

export const LanguageCodeSchema = z.enum(['ar-AE', 'ar-SA', 'en-US']);
export type LanguageCode = z.infer<typeof LanguageCodeSchema>;

/** Where a clinical fact came from. Surfaces must show this so AI narrative is never confused with the record. */
export const ProvenanceSchema = z.enum([
  'clinician-entered',
  'patient-reported',
  'imported',
  'device',
  'system',
  'ai-narrative',
]);
export type Provenance = z.infer<typeof ProvenanceSchema>;

export const IsoDateTime = z.string().datetime({ offset: true }).describe('ISO-8601 timestamp with offset');
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).describe('YYYY-MM-DD');

export const CodingSchema = z.object({
  system: z.string().describe('Code system URI, e.g. http://loinc.org'),
  code: z.string(),
  display: z.string().optional(),
});
export type Coding = z.infer<typeof CodingSchema>;

export const HumanNameSchema = z.object({
  given: z.string().min(1),
  family: z.string().min(1),
  middle: z.string().optional(),
  /** Full name in Arabic script when the primary name is Latin, or vice versa. */
  alternate: z.string().optional(),
});
export type HumanName = z.infer<typeof HumanNameSchema>;

export const ContactPointSchema = z.object({
  system: z.enum(['phone', 'email', 'sms', 'whatsapp']),
  value: z.string().min(1),
  use: z.enum(['home', 'work', 'mobile', 'other']).optional(),
  preferred: z.boolean().optional(),
});
export type ContactPoint = z.infer<typeof ContactPointSchema>;

export const AddressSchema = z.object({
  line1: z.string().optional(),
  line2: z.string().optional(),
  city: z.string().optional(),
  region: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().length(2).optional(),
});
export type Address = z.infer<typeof AddressSchema>;

/** Authenticated caller. Populated by the API layer from Cognito claims; never from request bodies. */
export const PrincipalSchema = z.object({
  tenantId: z.string().min(1),
  userId: z.string().min(1),
  roles: z.array(RoleSchema).min(1),
  /** Present only for PATIENT principals: the patient record they own. */
  patientId: z.string().optional(),
  /** Stable per login session; patient context is keyed on it. */
  sessionId: z.string().min(1),
  locale: LanguageCodeSchema.default('en-US'),
  displayName: z.string().optional(),
});
export type Principal = z.infer<typeof PrincipalSchema>;

export const PageSchema = z.object({
  limit: z.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type Page = z.infer<typeof PageSchema>;

export function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().optional() });
}
